<template>
  <div class="col-12 flex justify-center mt3 flex-column">
    <TerrainHeader :show-participant-card="true" />

    <div v-if="options.length && isCaptainPlaying" class="light-grey-bg px2 pb2 pt0 mb2">
      <h3>Récapitulatif</h3>

      <div class="flex white-bg p1">
        <div class="col-8 h6">
          x{{ 1 }} - Participation - {{ user.firstname }} {{ user.lastname }} {{ haveSubscription ? '(Abonné(e))' : '' }}
        </div>

        <div class="col-4 right-align">
          {{ haveSubscription ? '0' : computePrice(tariff.price) }} €
        </div>
      </div>

      <div v-for="option in options" :key="option.value" class="flex white-bg p1">
        <div class="col-1">
          x{{ option.quantity }}
        </div>
        <div class="col-7">
          {{ option.name }}
        </div>
        <div class="col-4 right-align">
          {{ computePrice(option.price.price * option.quantity) }} €
        </div>
      </div>

      <div v-if="hasPromo && !haveGymLibCode" class="flex white-bg p1">
        <div class="col-8 h6">
          x1 - Code promotionnel
        </div>
        <div class="col-4 right-align">
          {{ computePrice(discountPrice) }} €
        </div>
      </div>

      <div class="flex mt2 bold">
        <div class="col-8">Total</div>
        <div class="col-4 right-align">
          {{ computePrice(totalPrice) }} €
        </div>
      </div>
    </div>

    <div class="light-grey-bg mxn2 p1"></div>

    <div v-if="haveSubscription ||customerCards.length " class="white-bg p2 mt1">
      <h3 class="bold h4 mt0">Mes crédits disponibles</h3>

      <div v-if="haveSubscription" class="credit-card flex justify-between items-center p1 mb1">
        <div>
          <div class="bold h6">
            {{ subscriptionContract.productLabel.trim() }}<span class="xs-hide"> — N° {{ subscriptionContract.qrCode }}</span>
          </div>
          <div class="credit-included-message h6 mt1 p1">
            Votre 1re part est incluse dans votre abonnement.
          </div>
        </div>
      </div>

      <div v-if="customerCards.length" class="credit-card flex justify-between items-center p1 mb1">
        <div class="credit-select-wrapper">
          <span class="credit-select-label bold h6">
            {{ selectedCustomerCard.productLabel.trim() }}<span class="xs-hide"> — N° QR-{{ selectedCustomerCard.qrcode }}</span> — {{ selectedCustomerCard.availableCredit }} part{{ selectedCustomerCard.availableCredit > 1 ? 's' : '' }} restante{{ selectedCustomerCard.availableCredit > 1 ? 's' : '' }}
          </span>
          <select
            v-model="selectedCard"
            class="credit-select bold h6"
          >
            <option v-for="card in customerCards" :key="card.contractNumber" :value="card.contractNumber">
              {{ card.productLabel.trim() }} — {{ card.availableCredit }} part{{ card.availableCredit > 1 ? 's' : '' }} restante{{ card.availableCredit > 1 ? 's' : '' }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <div class="light-grey-bg mxn2 p1"></div>

    <div class="white-bg p2 mt1">
      <h3 class="bold h4 mt0">Ma participation</h3>

      <div class="flex justify-center items-center my2" style="gap: 2rem">
        <div class="flex flex-column items-center">
          <div class="priceCircle h4">{{ participation.amount }} €</div>
          <span class="h7 participation-label mt1">Montant</span>
        </div>
        <div v-if="participation.showUnits" class="flex flex-column items-center">
          <div class="priceCircle h4">{{ participation.units }} unité{{ participation.units > 1 ? 's' : '' }}</div>
          <span class="h7 participation-label mt1">Unités</span>
        </div>
      </div>

      <div class="mb3">
        <div class="flex justify-between items-end mb1">
          <span class="h6 flex items-center">
            <i class="icon icon-users mr1 violet"></i>
            Parts réservées
          </span>
          <span class="h6 bold violet">{{ participation.reservedParts }} / {{ participation.capacity }}</span>
        </div>
        <div class="parts-gauge flex light-grey-bg">
          <div
            v-for="n in participation.capacity"
            :key="n"
            class="parts-gauge__segment"
            :class="{
              'parts-gauge__segment--booked': n <= participation.bookedByOthers,
              'parts-gauge__segment--filled': n > participation.bookedByOthers && n <= participation.reservedParts
            }"
          ></div>
        </div>
        <p v-if="participation.remainingParts > 0" class="h7 mt1 participation-label">
          Encore {{ participation.remainingParts }} part{{ participation.remainingParts > 1 ? 's' : '' }} à pourvoir pour compléter la partie.
        </p>
      </div>

      <div class="parts-selector flex justify-between items-center p2 mb2">
        <span class="bold h6">Combien de parts souhaites-tu réserver ?</span>
        <div class="flex items-center">
          <button
            type="button"
            class="parts-selector__btn parts-selector__btn--minus violet-bg"
            :disabled="participation.partsQuantity <= 1"
            aria-label="Réduire le nombre de parts"
            @click="handlePartsQuantityChange(-1)"
          ></button>
          <div class="parts-selector__count bold h6">{{ participation.partsQuantity }}</div>
          <button
            type="button"
            class="parts-selector__btn violet-bg white"
            :disabled="participation.partsQuantity >= participation.capacity - participation.bookedByOthers"
            aria-label="Augmenter le nombre de parts"
            @click="handlePartsQuantityChange(1)"
          >
            <i class="icon icon-plus"></i>
          </button>
        </div>
      </div>

      <p v-if="!isInvite" class="h7 participation-label mb3">
        Capitaine, pense à partager le lien de la partie pour que d'autres joueurs complètent les parts restantes.
      </p>

      <div class="mb2 p2 info-block">
        <div class="bold h6 mb1">Informations importantes</div>
        <div class="h6">
          Tu garantis le remplissage du terrain et du paiement de la partie. L'annulation est gratuite <span class="bold">jusqu'à 48h avant le début.</span> Passé ce délai, la totalité du terrain reste due.
        </div>
      </div>

      <div class="p2 info-block">
        <div class="bold h6 mb1">Règlement le jour J</div>
        <div class="h6">
          Ta carte enregistrée règle tes parts ou complète tes crédits et abonnements. Elle sert aussi de garantie pour régler les parts restantes (joueurs manquants ou annulation hors délai). <span class="bold">Le prélèvement s'effectue le jour de la partie.</span>
        </div>
      </div>
    </div>

    <div class="light-grey-bg mxn2 p1"></div>

    <h3 class="bold h4">Moyen de paiement</h3>

    <!-- <div v-if="customerCards.length && !haveSubscription && !haveGymLibCode && !promoCode && !promoCampaign" class="mb2">
      <div class="flex col-12 px0 align-middle" @click="useMultipleCards = !useMultipleCards">
        <div class="mr1">
          <input id="useMultipleCards" v-model="useMultipleCards" type="checkbox" class="pointer">
        </div>
        <label class="h6 self-center">
          Utiliser Carte de X parts
        </label>
      </div>
      <select
        v-if="useMultipleCards"
        v-model="selectedCard"
        class="col-12 py1 pl2 mt1 border-select white-bg h6n"
        style="--showSelector: auto;"
        required
      >
        <option
          v-for="card in customerCards"
          :key="card.contractNumber"
          :value="card.contractNumber"
        >
          {{ card.qrcode }} - {{ card.productLabel }} : {{ card.contractStock }} stock
        </option>
      </select>
    </div> -->

    <h4 v-if="defaultCard" class="mt0 h6">Carte enregistrée</h4>
    <h4 v-else class="mt0 h6">Aucune carte enregistrée.</h4>

    <Button
      v-if="!defaultCard && !impersonateToken"
      :action="() => addSharegroopCard()"
      color="violet-bg mb2"
      :text="loading ? '' : 'Ajouter'"
      text-size="h6"
      :disabled="loading"
      :icon="loading ? 'icon icon-loader' : ''"
    />
    <div v-else class="flex items-center mb2">
      <div class="flex mb2">
        <i class="mt2 icon h3 mr2 ml1 icon-credit-card"></i>
      </div>
      <div class="col-6">
        <div class="flex xs-hide h6">
          Carte Bancaire XXXX {{ defaultCard?.last4 }}
        </div>
        <div class="flex h6 sm-hide md-hide lg-hide">
          Carte Bancaire XXXX {{ defaultCard?.last4 }}
        </div>
      </div>
      <div class="col-4">
        <Button
          v-if="!impersonateToken"
          :action="() => sharegroopChangeCard()"
          color="violet"
          border="violet-bd"
          :text="loading ? '' : 'Modifier'"
          text-size="h6"
          :disabled="loading"
          :icon="loading ? 'icon icon-loader' : ''"
        />
      </div>
    </div>

    <span v-if="defaultCard && !defaultCard.isCardValid" class="red h6">
      Votre carte bancaire expirera avant le début de la session. Merci de renseigner une nouvelle carte afin d’assurer le bon déroulement de votre réservation
    </span>

    <div v-if="!impersonateToken">
      <div
        class="col-12 white-bg px2"
      >
        <SalesCondition
          v-if="showSalesConditionModal"
          @hide-popup="showSalesConditionModal = false"
        />

        <div class="col-12 flex no-box px1 py2 items-center flex-column white-bg mt2">
          <div class="flex col-12 px0 align-middle" @click="cgi = !cgi">
            <div class="mr1">
              <input id="cgi" v-model="cgi" type="checkbox" class="pointer">
            </div>
            <label class="h6 self-center">
              Je certifie avoir lu et accepté <a class="h6 pointer cgi" :href="cgiHref" target="_blank"><span class="h6 pointer cgi underline">les conditions générales de vente</span></a> et <a class="h6 pointer cgi" :href="regHref" target="_blank"><span class="h6 pointer cgi underline">le règlement intérieur.</span></a>
            </label>
          </div>
          <span v-if="!cgi && showCgiError" class="h6 block col-12 alert no-box red lighter-grey-bg p1 cgiError mt2">
            <i class="mt2 icon h6 mr2 ml1 red icon-stop"></i>Merci de bien vouloir lire et accepter les conditions générales de ventes et le règlement intérieur
          </span>
        </div>
        <StepAction v-if="!pageLoading" text-size="h6" :text="btnLabel" :disabled="(!defaultCard || (defaultCard && !defaultCard.isCardValid)) || (useMultipleCards ? !selectedCard : false)" @handleClickAction="handlePaymentStep" />

        <Loader v-else />
      </div>
    </div>
    <div v-else>
      <Button text-size="h6" color="violet-bg" text="Vous êtes en mode impersonate, aucune action n'est autorisée" :disabled="true" />
    </div>
  </div>
</template>

<script setup>

import { computed, inject, ref, onBeforeMount, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { isBefore, endOfMonth } from 'date-fns';
import { useStore } from '@/store/index';

import Button from '@/components/elements/Button';
import TerrainHeader from '@/components/terrain/TerrainHeader.vue';
import StepAction from '@/components/terrain/StepAction.vue';
import Loader from '@/components/elements/Loader.vue';

import { tokenizeSharegroopCard, createCourtBooking, getParticipantActiveSubscriptionsInfo, getSharegroopCustomerInfo, getCustomerAmplifyCardContracts, addCourtShoppingCart, getServicesProduct, getCustomerAmplifySubscriptionContract } from '@/utils/requests';

import { computeCourtBookingDetails } from '@/utils/utils';
import { computeCourtDataLayer } from '@/utils/index';
import SalesCondition from '@/components/terrain/SalesCondition.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();

const advanceStep = inject('advanceStep');

const loading = ref(false);
const showSalesConditionModal = ref(false);
const pageLoading = ref(false);
const cgi = ref(false);
const showCgiError = ref(false);

const impersonateToken = store.state.impersonateData;

const customerCards = ref([]);

const session = computed(() => store.state.courtSettings.session);

const partsCapacity = computed(() => session.value.detail.capacitySoldOnline);
const alreadyBookedParts = computed(() => session.value.detail.sessionParticipantsCount);

const partsQuantity = ref(store.state.courtSettings.partsQuantity || 1);

const handlePartsQuantityChange = delta => {
  const newQuantity = partsQuantity.value + delta;

  if (newQuantity < 1 || newQuantity > partsCapacity.value - alreadyBookedParts.value) {
    return;
  }

  partsQuantity.value = newQuantity;
};

watch(partsQuantity, newQuantity => {
  store.commit('setCourtSettings', { ...store.state.courtSettings, partsQuantity: newQuantity });
}, { immediate: true });

const selectedCard = ref(store.state.courtSettings.selectedCardContract || null);

watch(selectedCard, newContract => {
  store.commit('setCourtSettings', { ...store.state.courtSettings, selectedCardContract: newContract });
});

const selectedCustomerCard = computed(() => customerCards.value.find(card => card.contractNumber === selectedCard.value) || customerCards.value[0]);

const hasUsableCard = computed(() => !!selectedCustomerCard.value && selectedCustomerCard.value.availableCredit > 0);

const user = computed(() => store.state.user);
const useMultipleCards = ref(!!user.value?.courtShoppingCart?.cardContract || false);

const userUuid = computed(() => user.value.uuid);

const hasPromo = computed(() => store.state.courtSettings.isPromoCodeValid);

const siteInfo = computed(() => store.state.siteInfo);

const options = computed(() => store.state.courtSettings.selectedOptions || []);

const isInvite = computed(() => store.state.courtSettings.isInvite);

const btnLabel = computed(() => (isInvite.value ? 'Rejoindre la partie' : 'Réserver'));

const isCaptainPlaying = computed(() => store.state.courtSettings.isCaptainPlaying);

const tariff = computed(() => store.state.courtSettings.tariff);

const cgiHref = computed(() => `${store.getters.siteURL}/cgi`);
const regHref = computed(() => `${store.getters.siteURL}/reglement-interieur`);

const defaultCard = computed(() => {
  const card = store.state.courtSettings?.sharegroopInfo?.methods?.find(method => method?.default);

  if (!card || (card && !card.card)) {
    return null;
  }

  const isCardValid = !isBefore(endOfMonth(new Date(`${card.card.expiryYear}-${card.card.expiryMonth}-01`)), new Date(session.value.detail.startDate));

  return { ...card?.card, isCardValid };
});

const offerInfo = computed(() => store.state.offerInfo);

const haveSubscription = computed(() => store.state.courtSettings.haveSubscription);
const subscriptionContract = computed(() => store.state.courtSettings.subscriptionContract);

const promoCode = computed(() => store.state.courtSettings.promoCode);
const promoCampaign = computed(() => store.state.courtSettings.promoCampaign);

const haveGymLibCode = computed(() => store.state.courtSettings.haveGymLibCode);

const sessionParticipantsAmount = computed(() => store.state.courtSettings?.session?.sessionParticipantsCount || (store.state.courtSettings?.session?.participants || []).length);
const reservedPlaces = computed(() => (isCaptainPlaying.value || isInvite.value ? sessionParticipantsAmount.value + 1 : sessionParticipantsAmount.value));

const remainingParticipantsAmount = computed(() => session.value.detail.max_participant - reservedPlaces.value);

const discountPrice = computed(() => {
  if (haveSubscription.value) {
    return 0;
  }
  const promoAmount = (store.state.courtSettings.tariff.price * store.state.courtSettings.promoCampaign.discount) / 100;

  return Math.floor(promoAmount);
});

const optionsPrice = computed(() => options.value.reduce((acc, option) => acc + option.price.price * option.quantity, 0));

const computePrice = price => (price !== 0 ? parseFloat(price / 100).toFixed(2) : 0);

const amountToPay = computed(() => {
  if (haveSubscription.value && hasUsableCard.value) {
    // The subscription covers one part, the card credit covers the next ones
    const partsToPay = Math.max(0, partsQuantity.value - 1 - selectedCustomerCard.value.availableCredit);

    return optionsPrice.value + partsToPay * tariff.value.price;
  }

  if (haveSubscription.value) {
    return optionsPrice.value + Math.max(0, partsQuantity.value - 1) * tariff.value.price;
  }

  if (hasUsableCard.value) {
    const partsToPay = Math.max(0, partsQuantity.value - selectedCustomerCard.value.availableCredit);

    return optionsPrice.value + partsToPay * tariff.value.price;
  }

  if (haveGymLibCode.value) {
    // what about options ???
    return optionsPrice.value;
  }

  if (promoCode.value && promoCampaign.value) {
    const promoAmount = (tariff.value.price * promoCampaign.value.discount) / 100;
    return Math.round(tariff.value.price - promoAmount) + optionsPrice.value;
  }

  if (!isCaptainPlaying.value && !isInvite.value) {
    return 0;
  }

  return optionsPrice.value + partsQuantity.value * tariff.value.price;
});

watch(amountToPay, newAmount => {
  store.commit('setCourtSettings', { ...store.state.courtSettings, amountToPay: newAmount });
}, { immediate: true });

const cardParticipationUnits = computed(() => {
  if (!hasUsableCard.value) {
    return 0;
  }

  const partsNotCoveredBySubscription = haveSubscription.value ? Math.max(0, partsQuantity.value - 1) : partsQuantity.value;

  return Math.min(partsNotCoveredBySubscription, selectedCustomerCard.value.availableCredit);
});

watch(cardParticipationUnits, newUnits => {
  store.commit('setCourtSettings', { ...store.state.courtSettings, cardParticipationUnits: newUnits });
}, { immediate: true });

const participation = computed(() => {
  const reservedParts = alreadyBookedParts.value + partsQuantity.value;

  return {
    amount: (amountToPay.value / 100).toFixed(2),
    units: cardParticipationUnits.value,
    showUnits: !!selectedCustomerCard.value,
    reservedParts,
    bookedByOthers: alreadyBookedParts.value,
    capacity: partsCapacity.value,
    remainingParts: partsCapacity.value - reservedParts,
    partsQuantity: partsQuantity.value
  };
});

const totalPrice = computed(() => optionsPrice.value + (haveSubscription.value ? 0 : offerInfo.value.detail.prices[0].price - (hasPromo.value ? discountPrice.value : 0)));

const fromProductStep = computed(() => store.state.courtSettings.fromProductStep);
const product = computed(() => store.state.product);

const tokenizeSharegroopCardRequest = async () => {
  loading.value = true;

  const body = computeCourtBookingDetails();

  delete body.isInternalSession;
  await addCourtShoppingCart(body);

  let successUrl = `${process.env.VUE_APP_UCPA_DOMAIN}/loisirs-reservation`;
  if (fromProductStep.value) {
    const { routeQueryActivities } = store.state.courtSettings;
    const { start_date, end_date } = store.state.courtSettings.session;
    successUrl += `/products?activities=${routeQueryActivities}&codes=${store.state.sessionsCodes.join(',')}&start_time=${start_date}&end_time=${end_date}&workspace=${siteInfo.value.workspace}&page_url=${store.state.pageUrl}`;
  } else {
    successUrl += `?workspace=${siteInfo.value.workspace}&product=${product.value.uuid}&offer=${offerInfo.value.horanet_product.horanet_product_code}&session=${session.value?.code || session.value.id}`;
  }

  if (store.state.courtSettings.isInvite) {
    successUrl = `${process.env.VUE_APP_UCPA_DOMAIN}/loisirs-reservation?workspace=${siteInfo.value.workspace}&product=${product.value.uuid}&offer=${offerInfo.value.horanet_product.horanet_product_code}&session=${session.value?.code || session.value.id}`;
  }
  successUrl += `&refresh=true&invite=${store.state.courtSettings.isInvite || false}`;

  const response = await tokenizeSharegroopCard(userUuid.value, siteInfo.value.workspace, successUrl);
  loading.value = false;
  window.location.href = response.data.tokenizeUrl;
};

const sharegroopChangeCard = async () => {
  await tokenizeSharegroopCardRequest();
};

const addSharegroopCard = async () => {
  await tokenizeSharegroopCardRequest();
};

const handlePaymentStep = async () => {
  computeCourtDataLayer('CourtPayment', { ...store.state.courtSettings }, route.fullPath, { ...store.state.product }, { ...store.state.offerInfo });

  if (!cgi.value) {
    showCgiError.value = true;
    return;
  }

  pageLoading.value = true;
  const body = computeCourtBookingDetails();

  const response = await createCourtBooking(body);

  if (response.success) {
    advanceStep();
  } else {
    store.commit('setErrorMessage', JSON.stringify(response.message, null, 4));
  }
  pageLoading.value = false;
};

onBeforeMount(async () => {
  const refresh = route?.query?.refresh === 'true';
  pageLoading.value = true;
  if (refresh || store.state.courtSettings.refresh) {
    loading.value = true;
    const promises = [getSharegroopCustomerInfo(store.state.user.uuid, store.state.siteInfo.workspace), getCustomerAmplifySubscriptionContract(store.state.siteInfo.workspace)];
    const [sgCustomerInfo, amplifyContracts] = await Promise.all(promises);
    let sessionServices = store.state.courtSettings?.sessionServices;

    if (!sessionServices) {
      sessionServices = await getServicesProduct([...new Set(store.state.courtSettings.session.products)]);
    }

    let customerSubscriptionContract = null;
    if (amplifyContracts && amplifyContracts.length) {
      const response = await getParticipantActiveSubscriptionsInfo(store.state.siteInfo.workspace);
      customerSubscriptionContract = amplifyContracts.find(amplifyContract => {
        const subscriptionCanBeUsedOnsession = store.state.courtSettings.session.detail.serviceIds.some(sessionService => amplifyContract.serviceIds.some(contractService => contractService.id.toString() === sessionService.id.toString()));

        if (!subscriptionCanBeUsedOnsession) {
          return false;
        }

        const contractMatchesSessionProduct = sessionServices.some(service => service.productRef === amplifyContract.productRef);

        if (!contractMatchesSessionProduct) {
          return false;
        }

        const sgSubscription = response.sharegroopCustomer.subscriptions.find(sgSub => sgSub.trackId === amplifyContract.orderId);

        if (sgSubscription) {
          return sgSubscription.status === 'active';
        }

        const stripeSubscription = response.stripeCustomer.subscriptions.find(stripeSub => stripeSub.orderUuid === amplifyContract.orderId || stripeSub?.metadata?.numeroSerieSupport === amplifyContract.qrcode);

        if (stripeSubscription) {
          return stripeSubscription.status === 'active';
        }

        return true;
      });
    }

    store.commit('setCourtSettings', { ...store.state.courtSettings, sharegroopInfo: sgCustomerInfo, haveSubscription: !!customerSubscriptionContract, subscriptionContract: customerSubscriptionContract, services: sessionServices });

    router.replace({ query: { } });
    loading.value = false;
  }

  // get customer cards
  customerCards.value = await getCustomerAmplifyCardContracts(store.state.courtSettings.session?.code || store.state.courtSettings.session.id, siteInfo.value.code_site_comptage);

  const storedCardIsStillEligible = customerCards.value.some(card => card.contractNumber === selectedCard.value);
  if (!storedCardIsStillEligible) {
    selectedCard.value = customerCards?.value[0]?.contractNumber || null;
  }

  pageLoading.value = false;
});

</script>

<style scoped>

.credit-card {
  border: 1px solid #c8cdd273;
  border-radius: 4px;
}

.credit-included-message {
  background: var(--light-grey);
  border-left: 3px solid var(--violet);
}

.credit-badge {
  border-radius: 20px;
  padding: 4px 12px;
  display: inline-block;
}

.credit-expiry {
  color: var(--medium-grey);
}

.participation-label {
  color: var(--medium-grey);
}

.parts-gauge {
  height: 12px;
  border-radius: 35px;
  overflow: hidden;
  gap: 4px;
  padding: 2px;
}

.parts-gauge__segment {
  flex: 1;
  border-radius: 35px;
  background-color: var(--medium-grey2);
}

.parts-gauge__segment--filled {
  background-color: var(--violet);
}

.parts-gauge__segment--booked {
  background-color: var(--light-cyan);
}

.parts-selector {
  border: 1px solid var(--violet);
  background-color: var(--lighter-grey);
}

.parts-selector__btn {
  border: none;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.15s;
}

.parts-selector__btn--minus {
  position: relative;
}

.parts-selector__btn--minus::before {
  content: '';
  width: 12px;
  height: 2px;
  background-color: var(--white);
}

.parts-selector__btn:disabled {
  opacity: 0.4;
  cursor: default;
}

.parts-selector__count {
  border-top: 1px solid var(--violet);
  border-bottom: 1px solid var(--violet);
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--white);
}

.info-block {
  background: var(--lighter-grey);
  border-left: 3px solid var(--violet);
}

.credit-select-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.credit-select-label {
  pointer-events: none;
}

.credit-select-label::after {
  content: '\25BE';
  display: inline-block;
  margin-left: 6px;
  color: var(--violet);
}

.credit-select {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 0;
  border: none;
  background: transparent;
  opacity: 0;
  cursor: pointer;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-right: 6px;
}

.priceCircle {
  border-radius: 50%;
  border: 3px solid var(--violet);
  width: 100px;
  height: 100px;
  font-weight: bold;
  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--violet);
}

@media (max-width: 52em) {
  .sticky-bottom-mobile {
    position: sticky;
    bottom: 0;
    z-index: 10;
    width: 100vw;
    margin-left: calc(-50vw + 50%);
    margin-bottom: calc(-50vw + 50%);
    box-sizing: border-box;
  }
}

</style>
